import { CircleAlert, CircleCheck, Loader2, X } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export interface BatchResult {
  invoice_id: string;
  filename: string;
  ok: boolean;
  postuplenie_number?: string | null;
  error?: string | null;
}

interface Props {
  isRunning: boolean;
  total: number;
  results: BatchResult[];
  onCancel: () => void;
  onClose: () => void;
  cancelRequested: boolean;
}

export function BatchProgressPanel({
  isRunning,
  total,
  results,
  onCancel,
  onClose,
  cancelRequested,
}: Props): React.ReactElement {
  const done = results.length;
  const ok = results.filter((r) => r.ok).length;
  const failed = done - ok;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            {isRunning ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <CircleCheck className="size-4 text-green-700" />
            )}
            {isRunning
              ? cancelRequested
                ? "Останавливаю…"
                : "Создаю документы в 1С…"
              : `Готово: ${ok} из ${total}`}
          </span>
          {isRunning ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={onCancel}
              disabled={cancelRequested}
            >
              Остановить
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={onClose}>
              <X className="size-4" />
              Закрыть
            </Button>
          )}
        </CardTitle>
        <div className="mt-2 h-2 w-full overflow-hidden rounded bg-muted">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {done} / {total} · успешно {ok}
          {failed > 0 && <> · ошибок {failed}</>}
        </p>
      </CardHeader>
      <CardContent className="max-h-64 space-y-1 overflow-y-auto text-xs">
        {results.map((r) => (
          <div
            key={r.invoice_id}
            className="flex items-start gap-2 rounded-md border bg-background p-2"
          >
            {r.ok ? (
              <CircleCheck className="mt-0.5 size-4 shrink-0 text-green-700" />
            ) : (
              <CircleAlert className="mt-0.5 size-4 shrink-0 text-destructive" />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{r.filename}</div>
              {r.ok ? (
                <div className="text-muted-foreground">
                  {r.postuplenie_number ?? "создан"}
                </div>
              ) : (
                <div className="text-destructive">{r.error ?? "ошибка"}</div>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
